/**
 * PP-1 — the default image loader for the crop editor.
 *
 * Plane-neutral and dependency-free: an object URL and an `<img>` element.
 * The same element is used for display AND for the canvas export, so the
 * orientation the user sees is exactly the orientation that is exported —
 * modern browsers apply EXIF orientation to `<img>` (CSS `image-orientation:
 * from-image` default), to `naturalWidth` / `naturalHeight`, and to
 * `drawImage` of an `HTMLImageElement` alike. The server re-applies `.rotate()`
 * on its own copy anyway and is the final authority.
 *
 * Kept in its own module so tests can replace it (`vi.mock`) — jsdom has no
 * `URL.createObjectURL` and never fires `load` on an image.
 */
export interface LoadedImage {
  /** What `drawImage` receives. */
  element: CanvasImageSource;
  /** Oriented natural size in pixels. */
  width: number;
  height: number;
  /** Something an `<img src>` can show, or null when only the element is usable. */
  src: string | null;
  /** Frees whatever the loader allocated (object URLs). Idempotent. */
  release: () => void;
}

export type ImageLoader = (file: File) => Promise<LoadedImage>;

export function loadImageFile(file: File): Promise<LoadedImage> {
  return new Promise((resolve, reject) => {
    const src = URL.createObjectURL(file);
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      URL.revokeObjectURL(src);
    };
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        release();
        reject(new Error('IMAGE_UNREADABLE'));
        return;
      }
      resolve({ element: img, width: img.naturalWidth, height: img.naturalHeight, src, release });
    };
    img.onerror = () => {
      release();
      reject(new Error('IMAGE_UNREADABLE'));
    };
    img.src = src;
  });
}
